import {AppBar, Button, MenuItem, Tab, Tabs, useMediaQuery} from '@material-ui/core';
import withWidth, {WithWidth} from '@material-ui/core/withWidth';
import AddAgentsMenu, {AddAgentsMobileMenu} from 'AgencyAdministration/components/AddAgentsMenu';
import PendingAgentRegistration from 'AgencyAdministration/components/PendingAgentRegistration';
import * as ModalActions from 'Core/ducks/modal';
import {useBreadcrumbs} from 'Core/hooks/useBreadcrumbs';
import {toEither} from 'Core/models/Async';
import * as E from 'fp-ts/lib/Either';
import * as O from 'fp-ts/lib/Option';
import {pipe} from 'fp-ts/lib/pipeable';
import React, {useEffect, useState} from 'react';
import {connect, useDispatch} from 'react-redux';
import {RouteComponentProps, useLocation, useRouteMatch, withRouter} from 'react-router-dom';
import {ReducerState} from 'rootReducer';
import ActionsMenu from 'Shared/components/PagedView/ActionsMenu';
import Column from 'Shared/components/PagedView/Column';
import {withStringFilterProperties} from 'Shared/components/PagedView/Filter';
import PagedView from 'Shared/components/PagedView/PagedView';
import RequireAtLeastOneScope from 'Shared/components/RequireAtLeastOneScope';
import RequireScope from 'Shared/components/RequireScope';
import withUserClaims, {WithClaimsProps} from 'Shared/components/withUserClaims';
import {Scope} from 'Shared/constants/Scope';
import {hasAccessToScopeByApplication} from 'Shared/utilities/applicationId';
import {parseQueryString} from 'Shared/utilities/parsers';
import {withValueChanged} from 'Shared/utilities/withValueChanged';
import {UserDto} from 'Users/api';
import DeleteUserConfirmationModal from 'Users/components/UserAdministration/DeleteUserConfirmationModal';
import ResetUserPassConfirmationModal from 'Users/components/UserAdministration/ResetUserPasswordConfirmationModal';
import * as AgentAdministrationActions from 'Users/ducks/agentAdministration';
import {AgentUserActions, AgentUserFilter, navigateToEdit} from 'Users/ducks/agentUser';
import * as IndependentAgentActions from 'Users/ducks/independentAgent';
import {UserProfileActions} from 'Users/ducks/userProfile';
import {Application} from 'Users/models/Application';
import {AsyncUserClaims} from 'Users/models/Claims';
import {ExclusivityStatus} from 'Users/models/User/ExclusivityStatus';
import {UserModel} from 'Users/models/UserModel';
import {
  selectCurrentIdentifier as selectCurrentEncryptedIdentifier,
  selectUserScopes,
} from 'Users/selectors/userProfileSelectors';
import './Agents.sass';
import {EmptyPreregisteredAgentFilter} from 'Users/models/PreregisteredAgent';

type AgentViewProps = ReturnType<typeof mapStateToProps> &
  typeof ModalActions &
  typeof UserProfileActions &
  typeof AgentUserActions & {
    impersonateAgent: typeof AgentAdministrationActions.impersonateAgent;
  } & {
    deletePreregistration: typeof IndependentAgentActions.deletePreregistration;
    getPreregisteredAgents: typeof IndependentAgentActions.getPreregisteredAgents;
    changePreregisteredAgentFilter: typeof IndependentAgentActions.changePreregisteredAgentFilter;
    resendEmail: typeof IndependentAgentActions.resendEmail;
  } & WithClaimsProps &
  WithWidth &
  RouteComponentProps;

type UserConfirmationState = {userId?: string; username?: string; user?: UserDto};

export const AgentsView = (props: AgentViewProps) => {
  const {
    showModal,
    agentUserFilter,
    claims,
    encryptedTaxId,
    userClaims,
    fetchAgentUsers,
    impersonateAgent,
    agents,
    changeAgentUserFilter,
    scopes,
  } = props;
  const match = useRouteMatch();
  const dispatch = useDispatch();
  useBreadcrumbs('Agents', match.url);

  const [deleteConfirmation, setDeleteConfirmation] = useState<O.Option<UserConfirmationState>>(O.none);
  const [currentTab, setCurrentTab] = useState(0);
  const {search} = useLocation();
  const isDesktop = useMediaQuery((theme: any) => theme.breakpoints.up('lg'));
  const [resetPassConfirmation, setResetPasswordConfirmation] = useState<O.Option<UserConfirmationState>>(O.none);

  const hideResetConfirmation = () => {
    setResetPasswordConfirmation(O.none);
    props.fetchAgentUsers(undefined, agentUserFilter);
  };
  const hideDeleteConfirmation = () => {
    setDeleteConfirmation(O.none);
    fetchAgentUsers(undefined, agentUserFilter);
  };

  const getQueryStringParameter = parseQueryString(search);
  const agentName = O.getOrElse(() => agentUserFilter.userName)(getQueryStringParameter('agentName'));
  const agentLastName = O.getOrElse(() => agentUserFilter.lastName)(getQueryStringParameter('lastName'));
  const changeFilter = withValueChanged(agentUserFilter, changeAgentUserFilter);

  const filterProperties = {
    changeFilter,
    filterData: agentUserFilter,
  };
  const onImpersonate = (userModel: UserModel) => impersonateAgent(claims.userName, userModel.userName!, [userModel.firstName!, userModel.lastName!]);

  useEffect(() => {
    changeAgentUserFilter({
      ...getAgentFilter(agentUserFilter, userClaims, scopes, encryptedTaxId),
      userName: agentName,
      lastName: agentLastName,
    });

  }, [encryptedTaxId, agentName, agentLastName]);

  useEffect(() => {
    fetchAgentUsers(undefined, agentUserFilter);
  }, [agentUserFilter, fetchAgentUsers]);

  useEffect(() => {
    return () => { 
      props.changePreregisteredAgentFilter(EmptyPreregisteredAgentFilter);
    }
  }, [props.changePreregisteredAgentFilter])

  const AgentsAppBar = (
    <div className="commissions__tabs">
      <AppBar position="static">
        <Tabs
          value={currentTab}
          onChange={(e, newValue) => {
            setCurrentTab(newValue);
          }}
        >
          <Tab key="agents-tab" label="Agents" />
          {hasAccessToScopeByApplication(scopes, Scope.SearchPreregistrationUsers, Application.Agent) && (
            <Tab key="pending-registrations-tab" label="Pending Registrations" />
          )}
        </Tabs>
      </AppBar>
    </div>
  );

  const Header = (
    <>
      {hasAccessToScopeByApplication(scopes, Scope.AddPreregistrationUsers, Application.Agent) &&
        (isDesktop ? (
          <div className="agents__actions">
            <AddAgentsMenu scopes={scopes} fetchAgentUsers={fetchAgentUsers} showModal={showModal} />
          </div>
        ) : (
          <>
            <AddAgentsMobileMenu scopes={scopes} fetchAgentUsers={fetchAgentUsers} showModal={showModal} />
          </>
        ))}
    </>
  );

  const getMenu = (agent: UserDto) =>
    [
      props.scopes.includes(Scope.EditAnyAgent) &&
        (!props.scopes.includes(Scope.SearchAgentUsersByExternalExclusivityStatus) ||
          agent.exclusivityStatus?.id === ExclusivityStatus.External) && (
          <MenuItem
            id={`edit-agent-btn-${agent.userName}`}
            key="agent-edit"
            onClick={() => dispatch(navigateToEdit(agent, props.encryptedTaxId))}
          >
            Edit
          </MenuItem>
        ),
      hasAccessToScopeByApplication(scopes, Scope.DeleteUser, Application.Agent) &&
        agent.exclusivityStatus?.id === ExclusivityStatus.External && (
          <MenuItem
            id={`delete-agent-btn-${agent.userName}`}
            key="agent-delete"
            onClick={() => {
              setDeleteConfirmation(O.some({user: agent}));
            }}
          >
            Delete
          </MenuItem>
        ),
      props.scopes.includes(Scope.ResetUserWithTemporaryPass) &&
        agent.exclusivityStatus?.id === ExclusivityStatus.External && (
          <MenuItem
            id={`reset-agent-btn-${agent.userName}`}
            key="agent-reset"
            onClick={() =>
              setResetPasswordConfirmation(
                O.some({
                  userId: agent.id!,
                  username: agent.userName!,
                })
              )
            }
          >
            Reset Password
          </MenuItem>
        ),
    ].filter(Boolean) as JSX.Element[];

  const renderAgentsView = () => (
    <>
      {O.isSome(resetPassConfirmation) && (
        <ResetUserPassConfirmationModal
          open
          userId={resetPassConfirmation.value.userId!}
          username={resetPassConfirmation.value.username!}
          onClose={hideResetConfirmation}
        />
      )}
      {O.isSome(deleteConfirmation) && (
        <DeleteUserConfirmationModal open user={deleteConfirmation.value.user!} onClose={hideDeleteConfirmation} />
      )}
      <PagedView
        pagedViewKey="agents"
        loadMore={page => fetchAgentUsers(page, agentUserFilter)}
        data={agents}
        additionalFilterProperties={[
          {
            title: 'Agent Number',
            filter: withStringFilterProperties<AgentUserFilter>(filterProperties, 'agentNumber'),
          },
        ]}
        actions={Header}
      >
        {columnProps => (
          <>
            <Column
              {...columnProps}
              filterProperties={withStringFilterProperties(filterProperties, 'userName')}
              title="User Name"
              default
              ddprivacy="mask"
            >
              {agent => agent.userName}
            </Column>
            <Column
              {...columnProps}
              filterProperties={withStringFilterProperties(filterProperties, 'firstName')}
              title="First Name"
              default
              ddprivacy="mask"
            >
              {agent => agent.firstName}
            </Column>
            <Column
              {...columnProps}
              filterProperties={withStringFilterProperties(filterProperties, 'lastName')}
              title="Last Name"
              default
            >
              {agent => agent.lastName}
            </Column>
            <Column {...columnProps} title="Actions" isActions default>
              {agent => (
                <>
                  <RequireScope scopes={props.scopes} scope={Scope.ImpersonateAgent}>
                    <Button
                      id={`agents-emulate-btn-${agent.userName}`}
                      color="primary"
                      onClick={() => onImpersonate(agent)}
                    >
                      Emulate
                    </Button>
                  </RequireScope>
                  <RequireAtLeastOneScope
                    scopes={[Scope.EditAnyAgent, Scope.UserDelete, Scope.ResetUserWithTemporaryPass]}
                  >
                    {getMenu(agent).length > 0 ? (
                      <ActionsMenu id={`agents-actions-menu-${agent.userName}`}>{getMenu(agent)}</ActionsMenu>
                    ) : (
                      <></>
                    )}
                  </RequireAtLeastOneScope>
                </>
              )}
            </Column>
          </>
        )}
      </PagedView>
    </>
  );

  const viewToRender =
    currentTab === 0 ? (
      renderAgentsView()
    ) : (
      <PendingAgentRegistration
        identifier={props.encryptedTaxId}
        deletePreregistration={props.deletePreregistration}
        getPreregisteredAgents={props.getPreregisteredAgents}
        tabHeader={AgentsAppBar}
        resendEmail={props.resendEmail}
        registrations={props.preregisteredAgentsPage}
        changePreregisteredAgentFilter={props.changePreregisteredAgentFilter}
        filter={props.preregisteredAgentFilter}
      />
    );

  return (
    <>
      {AgentsAppBar}
      {viewToRender}
    </>
  );
};

export const getAgentFilter = (
  currentFilter: AgentUserFilter,
  userClaims: AsyncUserClaims,
  scopes: Scope[],
  taxId?: string
): AgentUserFilter =>
  pipe(
    userClaims,
    toEither,
    E.fold(
      () => currentFilter,
      claims => {
        if (scopes.includes(Scope.SearchAgentUsersByRecruiterCode)) {
          const userTypes = claims && (claims.userTypes.filter(e => e.hasOwnProperty('recruiterCode'))[0] as any);
          const recruiterCode = userTypes && userTypes.recruiterCode;
          return {
            ...currentFilter,
            recruiterCodes: `${recruiterCode.value}|${recruiterCode.isNewYork}|${recruiterCode.isAhl}`,
          };
        }
        if (scopes.includes(Scope.SearchAgentUsersByIdentifier) && taxId) {
          return {
            ...currentFilter,
            encryptedIdentifier: taxId,
          };
        }
        if (scopes.includes(Scope.SearchAgentUsersByRecruiterCodes)) {
          const {fieldSalesLeaders} = claims.userTypes && (claims.userTypes[0] as any);
          const recruiterCodes = fieldSalesLeaders
            ? fieldSalesLeaders
                .map(
                  (fsl: any) => `${fsl.recruiterCode.value}|${fsl.recruiterCode.isNewYork}|${fsl.recruiterCode.isAhl}`
                )
                .join(',')
            : undefined;

          return {
            ...currentFilter,
            recruiterCodes,
          };
        }
        if (scopes.includes(Scope.SearchAgentUsersByLocation)) {
          const vipUserType = claims.userTypes && (claims.userTypes[0] as any);
          return {
            ...currentFilter,
            includeNewYork: vipUserType.isNewYork,
            includeAhl: vipUserType.isAhl,
          };
        } else {
          return currentFilter;
        }
      }
    )
  );

const mapStateToProps = (state: ReducerState) => ({
  agentUserFilter: state.user.agentUser.agentUserFilter,
  agents: state.user.agentUser.agents,
  scopes: selectUserScopes(state),
  userClaims: state.user.profile.userClaims,
  preregisteredAgentsPage: state.user.independentAgent.preregisteredAgentsPage,
  preregisteredAgentFilter: state.user.independentAgent.preregisteredAgentFilter,
  encryptedTaxId: selectCurrentEncryptedIdentifier(state),
});

export default connect(mapStateToProps, {
  ...AgentAdministrationActions,
  ...IndependentAgentActions,
  ...UserProfileActions,
  ...AgentUserActions,
  ...ModalActions,
})(withWidth()(withRouter(withUserClaims(AgentsView))));
